import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Mode from 'dashboard/components/GraphCanvasV2/behaviors/mode';
import EventBus from 'dashboard/components/GraphCanvasV2/event-bus';
import G6Wrapper from 'dashboard/components/GraphCanvasV2/G6Wrapper';
import { G6GraphUpdateMessage, Graph } from 'dashboard/models/graph';
import { Backend } from 'dashboard/models/graph-backend';
import { DemoBackend } from 'dashboard/models/graph-demo-backend';
import { GraphExplorer } from 'dashboard/models/graph-explorer';
import { QueryConfigArgs } from 'dashboard/models/graph-query';
import { RealBackend } from 'dashboard/models/graph-real-backend';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

interface GraphCanvasContextProviderProps {
  children: ReactNode;
  // Modes enabled. Initial mode set to be the first on the list.
  enabledModes?: Mode[];
  graph: Graph;
  viewTypeSwitchEnabled: boolean;
  viewType: string;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const GraphExplorerContext = createContext<GraphExplorer>(null);
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const BackendContext = createContext<Backend>(null);
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const EventBusContext = createContext<EventBus>(null);
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const G6WrapperContext = createContext<G6Wrapper>(null);

// TODO: At some point maybe move this to the GraphQueryBar if it is unnecessary
// to have in a semi-global context.
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export const QueryConfigContext = createContext<QueryConfigArgs>(null);

/**
 * TODO: Move g6Wrapper, EventBus, and Backend into this as well.
 */
const GraphCanvasContextProvider = (props: GraphCanvasContextProviderProps) => {
  const demoBackendEnabled = useFeatureFlag('dashboard:graph-demo-backend');
  const liveModeEnabled = useFeatureFlag('dashboard:graph-demo-live-mode');

  const graphExplorer = useRef<GraphExplorer>(
    new GraphExplorer(props.viewTypeSwitchEnabled, props.viewType),
  ).current;
  const serverSideDemoPayload = props.graph?.attributes['demo-backend-payload'];
  const [backend, setBackend] = useState<Backend>(new RealBackend(liveModeEnabled));
  const enabledModes = props.enabledModes || [Mode.SELECT, Mode.PAN];
  const eventBus = useRef<EventBus>(new EventBus(enabledModes)).current;
  const g6Wrapper = useRef<G6Wrapper>(new G6Wrapper(eventBus)).current;

  if (!g6Wrapper.isInitialized()) {
    // Note: This function should only be invoked once, since each of graphExplorer and
    //       g6Wrapper should be created once.
    g6Wrapper.initialize(graphExplorer);
  }

  // When this component unmounts, destroy eventBus to remove all bindings
  useEffect(() => {
    return () => {
      eventBus.destroy();
    };
  }, []);

  // -----------------------------------------------------------------------------------------
  // Canvas updates
  // -----------------------------------------------------------------------------------------
  const canvasUpdateCallback = useCallback(
    (message: G6GraphUpdateMessage) => {
      g6Wrapper.updateGraph(message);
    },
    [g6Wrapper],
  );
  useEffect(() => {
    graphExplorer.setCanvasUpdateCallback(canvasUpdateCallback);
  }, [graphExplorer, canvasUpdateCallback]);

  // -----------------------------------------------------------------------------------------
  // Keyboard Shortcuts for changing modes
  // -----------------------------------------------------------------------------------------
  const handleSpaceKeyUp = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).nodeName === 'INPUT') return;
    // Switching modes
    if (e.code === 'Space') {
      if (eventBus.mode !== Mode.SELECT && enabledModes.includes(Mode.SELECT)) {
        eventBus.setMode(Mode.SELECT);
      }
    }
  };

  const handleSpaceKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).nodeName === 'INPUT') return;
      // Switching modes
      if (e.code === 'Space') {
        if (eventBus.mode !== Mode.PAN && enabledModes.includes(Mode.PAN)) {
          eventBus.setMode(Mode.PAN);
        }
      }
    },
    [eventBus],
  );

  useEffect(() => {
    window.addEventListener('keyup', handleSpaceKeyUp);
    window.addEventListener('keydown', handleSpaceKeyDown);

    return () => {
      window.removeEventListener('keyup', handleSpaceKeyUp);
      window.removeEventListener('keydown', handleSpaceKeyDown);
    };
  });

  // -----------------------------------------------------------------------------------------
  // Keyboard Shortcuts for changing backends
  // -----------------------------------------------------------------------------------------
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).nodeName === 'INPUT') return;
      // Switching modes
      if (e.ctrlKey === true) {
        // Ctrl + b real Backend
        // Ctrl + j local storage Javascript demo backend
        // Ctrl + e sErver side demo backend
        if (e.key === 'b') {
          setBackend(new RealBackend(liveModeEnabled));
        } else if (e.key === 'j' && demoBackendEnabled) {
          const demoBackend = new DemoBackend(liveModeEnabled);
          demoBackend.readFromLocalStorage();
          setBackend(demoBackend);
        } else if (e.key === 'e' && demoBackendEnabled) {
          const demoBackend = new DemoBackend(liveModeEnabled);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          demoBackend.loadFromServer(serverSideDemoPayload);
          setBackend(demoBackend);
        }
      }
    },
    [demoBackendEnabled, liveModeEnabled, serverSideDemoPayload],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  return (
    <EventBusContext.Provider value={eventBus}>
      <BackendContext.Provider value={backend}>
        <G6WrapperContext.Provider value={g6Wrapper}>
          <GraphExplorerContext.Provider value={graphExplorer}>
            {props.children}
          </GraphExplorerContext.Provider>
        </G6WrapperContext.Provider>
      </BackendContext.Provider>
    </EventBusContext.Provider>
  );
};

export default GraphCanvasContextProvider;
